/**
 * LanguageContext - Seul responsable de la langue active
 *
 * Logique :
 * - NON-LOGUE : langue déduite du domainConfig
 *   - Domaine mono-langue (1 seule langue) : force cette langue
 *   - Domaine multi-langues : détection navigateur dans la liste, fallback sur defaultLanguage
 *   - Changement manuel via LanguageSwitcher (état local + localStorage)
 * - LOGUE : userLanguage de PreferencesContext prend la priorité absolue
 *
 * Expose : language, setLanguage, t, translate, languages, visibleLanguages,
 *          visibleLanguageCodes, setVisibleLanguageCodes, showLanguageSwitcher, setShowLanguageSwitcher
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { translations, languages, defaultLanguage, getTranslation } from '../i18n';
import { usePreferences } from './PreferencesContext';

const LanguageContext = createContext();

// All available language codes
const ALL_LANGUAGE_CODES = ['fr', 'en', 'nl', 'de', 'es', 'it', 'pt', 'el'];

// LocalStorage key for manual language override (non-logged users)
const MANUAL_LANG_KEY = 'cabazaManualLanguage';

/**
 * Detect browser language from the available list
 * @param {string[]} availableCodes - Language codes to choose from
 * @returns {string|null} Matched language code or null
 */
const getBrowserLanguage = (availableCodes) => {
    try {
        const browserLangs = navigator.languages || [navigator.language || navigator.userLanguage];
        for (const browserLang of browserLangs) {
            const code = browserLang.split('-')[0].toLowerCase();
            if (availableCodes.includes(code)) {
                return code;
            }
        }
    } catch (e) {
        // Ignore
    }
    return null;
};

/**
 * Compute initial language from domainConfig (for non-logged users)
 * @param {Object} domainConfig
 * @returns {string} Language code
 */
const computeDomainLanguage = (domainConfig) => {
    if (!domainConfig || !domainConfig.found) {
        return defaultLanguage;
    }

    const domainLangs = (domainConfig.languages || [])
        .map(l => (l.code || l.CODE || '').toLowerCase())
        .filter(c => c && translations[c]);

    const domainDefault = (domainConfig.defaultLanguage || '').toLowerCase();

    if (domainLangs.length === 1) {
        // Single language domain: FORCE that language, ignore manual override
        return domainLangs[0];
    }

    if (domainLangs.length > 1) {
        // Multi-language domain: check manual override first, then browser detection
        try {
            const manual = localStorage.getItem(MANUAL_LANG_KEY);
            if (manual && translations[manual] && domainLangs.includes(manual)) {
                return manual;
            }
        } catch (e) {
            // Ignore
        }
        const browserLang = getBrowserLanguage(domainLangs);
        if (browserLang) return browserLang;
        if (domainDefault && translations[domainDefault]) return domainDefault;
        return domainLangs[0];
    }

    // No languages in config: use domain default or global default
    if (domainDefault && translations[domainDefault]) return domainDefault;
    return defaultLanguage;
};

/**
 * Compute visible language codes from domainConfig
 * @param {Object} domainConfig
 * @returns {string[]}
 */
const computeDomainVisibleLanguages = (domainConfig) => {
    if (!domainConfig || !domainConfig.found) {
        return ALL_LANGUAGE_CODES;
    }

    const domainLangs = (domainConfig.languages || [])
        .map(l => (l.code || l.CODE || '').toLowerCase())
        .filter(c => c && translations[c]);

    return domainLangs.length > 0 ? domainLangs : ALL_LANGUAGE_CODES;
};

export const LanguageProvider = ({ children, domainConfig }) => {
    // Read logged-in user preferences (null when not logged)
    const {
        userLanguage,
        userVisibleLanguageCodes,
        showLanguageSwitcher: prefShowLanguageSwitcher,
        updateLanguage: prefUpdateLanguage,
        updateVisibleLanguageCodes: prefUpdateVisibleLanguageCodes,
        updateShowLanguageSwitcher: prefUpdateShowLanguageSwitcher
    } = usePreferences();

    // Local language state for non-logged users (initialized from domainConfig)
    const [localLanguage, setLocalLanguage] = useState(() => computeDomainLanguage(domainConfig));

    // Effective language: logged-in user preference takes absolute priority
    const language = userLanguage || localLanguage;

    // Effective visible language codes
    const visibleLanguageCodes = useMemo(() => {
        if (userVisibleLanguageCodes) return userVisibleLanguageCodes;
        return computeDomainVisibleLanguages(domainConfig);
    }, [userVisibleLanguageCodes, domainConfig]);

    // Show language switcher (only for logged-in users; non-logged: show if multi-language domain)
    const showLanguageSwitcher = useMemo(() => {
        if (userLanguage) {
            // Logged-in: use preference
            return prefShowLanguageSwitcher ?? false;
        }
        // Non-logged: show if domain has multiple languages
        return visibleLanguageCodes.length > 1;
    }, [userLanguage, prefShowLanguageSwitcher, visibleLanguageCodes]);

    // Get current translations object
    const t = translations[language] || translations[defaultLanguage];

    // Change language
    const setLanguage = useCallback((lang) => {
        if (!translations[lang]) return;

        if (userLanguage) {
            // Logged-in: delegate to PreferencesContext (saves to file + DB)
            prefUpdateLanguage(lang);
        } else {
            // Non-logged: update local state + localStorage
            setLocalLanguage(lang);
            try {
                localStorage.setItem(MANUAL_LANG_KEY, lang);
            } catch (e) {
                // Ignore
            }
        }
    }, [userLanguage, prefUpdateLanguage]);

    // Translate function that supports nested keys like 'nav.discover'
    const translate = useCallback((key, params = {}) => {
        const value = getTranslation(t, key);

        if (typeof value === 'function') {
            return value(params);
        }

        if (typeof value === 'string' && Object.keys(params).length > 0) {
            return Object.entries(params).reduce((str, [k, val]) => {
                return str.replace(new RegExp(`{${k}}`, 'g'), val);
            }, value);
        }

        return value;
    }, [t]);

    // Set html lang attribute on language change
    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    // Set visible languages (only for logged-in users)
    const setVisibleLanguageCodes = useCallback((codes) => {
        if (userLanguage) {
            prefUpdateVisibleLanguageCodes(codes);
        }
    }, [userLanguage, prefUpdateVisibleLanguageCodes]);

    // Set showLanguageSwitcher (only for logged-in users)
    const setShowLanguageSwitcher = useCallback((show) => {
        if (userLanguage) {
            prefUpdateShowLanguageSwitcher(show);
        }
    }, [userLanguage, prefUpdateShowLanguageSwitcher]);

    // Get filtered languages for dropdown
    const visibleLanguages = languages.filter(l => visibleLanguageCodes.includes(l.code));

    const value = {
        language,
        setLanguage,
        t,
        translate,
        languages,
        visibleLanguages,
        visibleLanguageCodes,
        setVisibleLanguageCodes,
        showLanguageSwitcher,
        setShowLanguageSwitcher,
        translations
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

// Custom hook to use language context
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Shorthand hook for just translations
export const useTranslation = () => {
    const { t, translate, language } = useLanguage();
    return { t, translate, language };
};

export default LanguageContext;
